import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateFormat';

const AttendanceView = ({ attendance, stats, onBack }) => {
  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ px: 2 }}>
        {/* Info Message */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.675rem',
              lineHeight: 1.6,
              mb: 1,
              px: 2,
            }}
          >
            The statistics below show the last 14 days of attendance. Keep track of your child's attendance status to ensure they maintain good attendance throughout the academic year.
          </Typography>
        </Box>

        {stats && (
          <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 1, flexWrap: 'wrap' }}>
                {/* Present */}
                <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                  <Typography variant="h6" color="success.main" fontWeight={700}>
                    {stats.total_present || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Present
                  </Typography>
                </Box>
                
                {/* Absent */}
                <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                  <Typography variant="h6" color="error.main" fontWeight={700}>
                    {stats.total_absent || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Absent
                  </Typography>
                </Box>
                
                {/* Total Days */}
                <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>
                    {stats.total_records || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Days
                  </Typography>
                </Box>
                
                {/* Percentage */}
                {stats.total_percentage !== undefined && (
                  <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {stats.total_percentage}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Total
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}


        {attendance.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No attendance records found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {attendance.map((record, idx) => {
              const date = new Date(record.date);
              const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
              const formattedDate = formatDate(record.date);
              const status = record.status || 'Pending';
              const isPending = status === 'Pending';
              
              return (
                <Card
                  key={record.id || idx}
                  sx={{
                    borderRadius: 2,
                    boxShadow: 1,
                    py: 0.5,
                  }}
                >
                  <CardContent sx={{ py: '8px !important', px: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body2" 
                          fontWeight={600}
                          sx={{ 
                            minWidth: 45,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          {weekday}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          sx={{ 
                            flex: 1,
                            fontSize: '0.875rem',
                          }}
                        >
                          {formattedDate}
                        </Typography>
                      </Box>
                      <Chip
                        icon={
                          status === 'Present' ? <CheckCircleIcon /> : 
                          status === 'Absent' ? <CancelIcon /> : 
                          <AccessTimeIcon />
                        }
                        label={status}
                        size="small"
                        color={
                          status === 'Present' ? 'success' : 
                          status === 'Absent' ? 'error' : 
                          'warning'
                        }
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          '& .MuiChip-icon': {
                            fontSize: '16px',
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AttendanceView;

